import React, { useRef, useEffect, useContext, createContext } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import IconButton from './IconButton';
import {
  DEFAULT_SECTION_LABELS,
  newCertificationId,
  newCustomSectionId,
  newEducationId,
  newExperienceId,
  newLanguageId,
  newProjectId,
} from '../utils/resumeExtract';

function fieldHighlightClass(activeKeys, key, isActive) {
  if (!activeKeys?.has(key)) return '';
  return isActive ? 'editor-field-highlight editor-field-highlight-active' : 'editor-field-highlight';
}

const FieldProposalContext = createContext({
  byKey: new Map(),
  onPick: () => {},
  onDismiss: () => {},
  firstKey: null,
});

function optionLabel(option) {
  if (!option) return '';
  if (option.text) return option.text;
  const item = option.item || {};
  const head = [item.title, item.company, item.degree, item.school, item.name, item.language, item.label, item.email]
    .filter(Boolean)
    .join(' — ');
  const extra = Array.isArray(item.bullets) && item.bullets.length
    ? item.bullets[0]
    : (item.body || item.description || '');
  return [head, extra].filter(Boolean).join(': ') || 'Use this version';
}

function FieldOptionPopover({ proposal, onPick, onDismiss }) {
  const ref = useRef(null);
  useEffect(() => {
    const onPointer = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onDismiss(proposal.id);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') onDismiss(proposal.id);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onDismiss, proposal.id]);

  return (
    <div
      className="field-option-popover"
      ref={ref}
      role="dialog"
      aria-label="Choose a version"
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="field-option-popover-head">
        <span>Choose a version</span>
        <button type="button" className="field-option-hide" aria-label="Hide choices" onClick={() => onDismiss(proposal.id)}>
          Hide
        </button>
      </div>
      <ul>
        {(proposal.options || []).map((option, index) => (
          <li key={`${proposal.id}-${index}`}>
            <button type="button" onClick={() => onPick(proposal, index)}>
              {optionLabel(option)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OptionAnchor({ proposalKey, children }) {
  const { byKey, onPick, onDismiss, firstKey } = useContext(FieldProposalContext);
  const proposal = proposalKey ? byKey.get(proposalKey) : null;
  const anchorRef = useRef(null);
  useEffect(() => {
    if (proposal && proposalKey === firstKey && anchorRef.current) {
      anchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [proposal, proposalKey, firstKey]);
  if (!proposal) return children || null;
  return (
    <div className={`field-option-anchor${children ? '' : ' field-option-anchor-solo'}`} ref={anchorRef}>
      {children}
      <FieldOptionPopover proposal={proposal} onPick={onPick} onDismiss={onDismiss} />
    </div>
  );
}

function Field({ label, proposalKey, children }) {
  return (
    <label className="editor-field">
      <span>{label}</span>
      <OptionAnchor proposalKey={proposalKey}>{children}</OptionAnchor>
    </label>
  );
}

const SECTION_HINTS = {
  contact: 'How recruiters reach you. These details stay on the form and are not sent to AI.',
  summary: 'Two or three sentences on the work you do and the results you bring.',
  experience: 'Each role, with dates and bullets that show an outcome.',
  education: 'Degrees, schools, and the years you attended.',
  skills: 'Tools and strengths, separated by commas.',
  certifications: 'Licenses and certificates worth listing for the role.',
  languages: 'Languages you can work in, and how fluent you are.',
  projects: 'Work you want to show beyond a job title.',
};

function scoreTone(score) {
  if (score >= 75) return 'good';
  if (score >= 50) return 'mid';
  return 'low';
}

function SectionTitle({ value, onChange, hint, score, actions, labelKey }) {
  return (
    <div className="editor-section-head">
      <div className="editor-section-title">
        <OptionAnchor proposalKey={labelKey}>
          <input
            className="section-label-input"
            value={value}
            aria-label="Section title"
            onChange={(e) => onChange(e.target.value)}
          />
        </OptionAnchor>
        {hint ? <p className="editor-section-hint">{hint}</p> : null}
      </div>
      <div className="editor-section-actions">
        {score != null && (
          <span className={`section-score-pill section-score-${scoreTone(score)}`} title="Section score">
            {score}
          </span>
        )}
        {actions}
      </div>
    </div>
  );
}

const ResumeEditor = ({
  structured,
  onChange,
  activeSuggestionKeys,
  activeSuggestionKey,
  fileName,
  onFileNameChange,
  sectionScores,
  fieldProposals,
  onPickFieldOption,
  onDismissFieldOption,
}) => {
  const highlightRef = useRef(null);

  useEffect(() => {
    if (activeSuggestionKey && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeSuggestionKey]);

  const s = structured || {
    contact: {},
    section_labels: { ...DEFAULT_SECTION_LABELS },
    summary: '',
    experience: [],
    education: [],
    skills: [],
    certifications: [],
    languages: [],
    projects: [],
    custom_sections: [],
  };

  const labels = { ...DEFAULT_SECTION_LABELS, ...(s.section_labels || {}) };

  const update = (patch) => onChange({ ...s, ...patch });

  const setLabel = (key, value) => {
    update({ section_labels: { ...labels, [key]: value } });
  };

  const updateContact = (field, value) => {
    update({ contact: { ...s.contact, [field]: value } });
  };

  const updateExperience = (idx, patch) => {
    const experience = [...(s.experience || [])];
    experience[idx] = { ...experience[idx], ...patch };
    update({ experience });
  };

  const updateBullet = (expIdx, bulletIdx, value) => {
    const experience = [...(s.experience || [])];
    const bullets = [...(experience[expIdx].bullets || [])];
    bullets[bulletIdx] = value;
    experience[expIdx] = { ...experience[expIdx], bullets };
    update({ experience });
  };

  const addExperience = () => {
    update({
      experience: [
        ...(s.experience || []),
        { id: newExperienceId(), title: '', company: '', location: '', dates: '', bullets: [''] },
      ],
    });
  };

  const removeExperience = (idx) => {
    update({ experience: (s.experience || []).filter((_, i) => i !== idx) });
  };

  const addBullet = (expIdx) => {
    const experience = [...(s.experience || [])];
    experience[expIdx] = {
      ...experience[expIdx],
      bullets: [...(experience[expIdx].bullets || []), ''],
    };
    update({ experience });
  };

  const removeBullet = (expIdx, bulletIdx) => {
    const experience = [...(s.experience || [])];
    experience[expIdx] = {
      ...experience[expIdx],
      bullets: (experience[expIdx].bullets || []).filter((_, i) => i !== bulletIdx),
    };
    update({ experience });
  };

  const updateEducation = (idx, patch) => {
    const education = [...(s.education || [])];
    education[idx] = { ...education[idx], ...patch };
    update({ education });
  };

  const addEducation = () => {
    update({
      education: [...(s.education || []), { id: newEducationId(), degree: '', school: '', dates: '' }],
    });
  };

  const removeEducation = (idx) => {
    update({ education: (s.education || []).filter((_, i) => i !== idx) });
  };

  const updateListItem = (key, idx, patch) => {
    const list = [...(s[key] || [])];
    list[idx] = { ...list[idx], ...patch };
    update({ [key]: list });
  };

  const addCertification = () => {
    update({
      certifications: [...(s.certifications || []), { id: newCertificationId(), name: '', issuer: '', date: '' }],
    });
  };

  const addLanguage = () => {
    update({
      languages: [...(s.languages || []), { id: newLanguageId(), language: '', proficiency: '' }],
    });
  };

  const addProject = () => {
    update({
      projects: [...(s.projects || []), { id: newProjectId(), name: '', description: '', technologies: [] }],
    });
  };

  const addCustomSection = () => {
    update({
      custom_sections: [
        ...(s.custom_sections || []),
        { id: newCustomSectionId(), label: 'New section', body: '' },
      ],
    });
  };

  const skillsText = (s.skills || []).join(', ');
  const hl = (key) => fieldHighlightClass(activeSuggestionKeys, key, activeSuggestionKey === key);
  const scoreFor = (key) => {
    const value = sectionScores?.[key];
    return value == null || value === '' ? null : value;
  };
  const proposalMap = new Map();
  for (const proposal of fieldProposals || []) {
    const key = chatProposalKey(proposal, s);
    if (key && !proposalMap.has(key)) proposalMap.set(key, proposal);
  }
  const firstProposalKey = proposalMap.keys().next().value || null;

  return (
    <FieldProposalContext.Provider
      value={{
        byKey: proposalMap,
        onPick: onPickFieldOption || (() => {}),
        onDismiss: onDismissFieldOption || (() => {}),
        firstKey: firstProposalKey,
      }}
    >
    <div className="resume-editor">
      <section className="editor-section">
        <div className="editor-section-head">
          <div className="editor-section-title">
            <h2 className="editor-section-heading">Resume title</h2>
            <p className="editor-section-hint">The name shown in your resume list. Chat can rename it.</p>
          </div>
        </div>
        <Field label="Title" proposalKey="title">
          <input
            type="text"
            value={fileName || ''}
            onChange={(e) => onFileNameChange(e.target.value)}
            placeholder="Software Engineer Resume"
          />
        </Field>
      </section>

      <section className="editor-section" id="resume-section-contact">
        <SectionTitle
          value={labels.contact}
          hint={SECTION_HINTS.contact}
          labelKey="label.contact"
          onChange={(value) => setLabel('contact', value)}
        />
        <div className="editor-grid">
          <Field label="Full name" proposalKey="contact.name">
            <input className={hl('contact.name')} value={s.contact?.name || ''} onChange={(e) => updateContact('name', e.target.value)} />
          </Field>
          <Field label="Email" proposalKey="contact.email">
            <input className={hl('contact.email')} type="email" value={s.contact?.email || ''} onChange={(e) => updateContact('email', e.target.value)} />
          </Field>
          <Field label="Phone" proposalKey="contact.phone">
            <input className={hl('contact.phone')} value={s.contact?.phone || ''} onChange={(e) => updateContact('phone', e.target.value)} />
          </Field>
          <Field label="Location" proposalKey="contact.location">
            <input value={s.contact?.location || ''} onChange={(e) => updateContact('location', e.target.value)} />
          </Field>
          <Field label="LinkedIn URL" proposalKey="contact.linkedin">
            <input className={hl('contact.linkedin')} value={s.contact?.linkedin || ''} onChange={(e) => updateContact('linkedin', e.target.value)} />
          </Field>
        </div>
        <p className="editor-note">Name, email, phone, location, and profile links stay in this form. They are not sent when you score or review.</p>
      </section>

      <section className="editor-section" id="resume-section-summary">
        <SectionTitle
          value={labels.summary}
          hint={SECTION_HINTS.summary}
          score={scoreFor('summary')}
          labelKey="label.summary"
          onChange={(value) => setLabel('summary', value)}
        />
        <OptionAnchor proposalKey="summary">
          <textarea
            ref={activeSuggestionKey === 'summary' ? highlightRef : null}
            className={hl('summary')}
            rows={5}
            value={s.summary || ''}
            onChange={(e) => update({ summary: e.target.value })}
            placeholder="Two or three sentences on the kind of work you do and the results you have delivered."
          />
        </OptionAnchor>
      </section>

      <section className="editor-section" id="resume-section-experience">
        <SectionTitle
          value={labels.experience}
          hint={SECTION_HINTS.experience}
          score={scoreFor('experience')}
          labelKey="label.experience"
          onChange={(value) => setLabel('experience', value)}
          actions={(
            <IconButton icon={FiPlus} label="Add role" size="sm" onClick={addExperience} />
          )}
        />
        <OptionAnchor proposalKey="experience.add" />
        {(s.experience || []).length === 0 && <p className="editor-note">Add each role with a title, company, location, dates, and bullets.</p>}
        {(s.experience || []).map((exp, idx) => {
          const expKey = `experience.${exp.id}`;
          const isExpActive = activeSuggestionKey === expKey || activeSuggestionKey?.startsWith(`${expKey}.`);
          return (
            <div
              key={exp.id || idx}
              ref={isExpActive && !activeSuggestionKey?.includes('.bullet.') ? highlightRef : null}
              className={`experience-block ${hl(expKey)}`}
            >
              <div className="entry-toolbar">
                <IconButton icon={FiPlus} label="Add bullet" size="sm" onClick={() => addBullet(idx)} />
                <IconButton icon={FiTrash2} label="Remove role" size="sm" variant="danger" onClick={() => removeExperience(idx)} />
              </div>
              <OptionAnchor proposalKey={expKey} />
              <div className="editor-grid">
                <Field label="Job title" proposalKey={`experience.${exp.id}.title`}>
                  <input value={exp.title || ''} onChange={(e) => updateExperience(idx, { title: e.target.value })} />
                </Field>
                <Field label="Company" proposalKey={`experience.${exp.id}.company`}>
                  <input value={exp.company || ''} onChange={(e) => updateExperience(idx, { company: e.target.value })} />
                </Field>
                <Field label="Location" proposalKey={`experience.${exp.id}.location`}>
                  <input value={exp.location || ''} onChange={(e) => updateExperience(idx, { location: e.target.value })} placeholder="City or region" />
                </Field>
                <Field label="Dates" proposalKey={`experience.${exp.id}.dates`}>
                  <input value={exp.dates || ''} onChange={(e) => updateExperience(idx, { dates: e.target.value })} placeholder="2020 – 2024" />
                </Field>
              </div>
              <span className="editor-field-caption">Achievements</span>
              {(exp.bullets || ['']).map((bullet, bIdx) => {
                const bulletKey = `${expKey}.bullet.${bIdx}`;
                return (
                  <div key={bulletKey} className="bullet-row">
                    <OptionAnchor proposalKey={bulletKey}>
                      <textarea
                        ref={activeSuggestionKey === bulletKey ? highlightRef : null}
                        className={hl(bulletKey)}
                        rows={2}
                        value={bullet}
                        onChange={(e) => updateBullet(idx, bIdx, e.target.value)}
                        placeholder="Result with a number, for example reduced latency 40%."
                      />
                    </OptionAnchor>
                    <IconButton icon={FiTrash2} label="Remove bullet" size="sm" variant="danger" onClick={() => removeBullet(idx, bIdx)} />
                  </div>
                );
              })}
            </div>
          );
        })}
      </section>

      <section className="editor-section" id="resume-section-education">
        <SectionTitle
          value={labels.education}
          hint={SECTION_HINTS.education}
          score={scoreFor('education')}
          labelKey="label.education"
          onChange={(value) => setLabel('education', value)}
          actions={(
            <IconButton icon={FiPlus} label="Add education" size="sm" onClick={addEducation} />
          )}
        />
        <OptionAnchor proposalKey="education.add" />
        {(s.education || []).map((edu, idx) => {
          const eduKey = `education.${edu.id}`;
          return (
            <div key={edu.id || idx} ref={activeSuggestionKey === eduKey ? highlightRef : null} className={`experience-block ${hl(eduKey)}`}>
              <div className="entry-toolbar">
                <IconButton icon={FiTrash2} label="Remove education" size="sm" variant="danger" onClick={() => removeEducation(idx)} />
              </div>
              <OptionAnchor proposalKey={eduKey} />
              <div className="editor-grid">
                <Field label="Degree" proposalKey={`education.${edu.id}.degree`}>
                  <input value={edu.degree || ''} onChange={(e) => updateEducation(idx, { degree: e.target.value })} />
                </Field>
                <Field label="School" proposalKey={`education.${edu.id}.school`}>
                  <input value={edu.school || ''} onChange={(e) => updateEducation(idx, { school: e.target.value })} />
                </Field>
                <Field label="Dates" proposalKey={`education.${edu.id}.dates`}>
                  <input value={edu.dates || ''} onChange={(e) => updateEducation(idx, { dates: e.target.value })} />
                </Field>
              </div>
            </div>
          );
        })}
      </section>

      <section className="editor-section" id="resume-section-skills">
        <SectionTitle
          value={labels.skills}
          hint={SECTION_HINTS.skills}
          score={scoreFor('skills')}
          labelKey="label.skills"
          onChange={(value) => setLabel('skills', value)}
        />
        <Field label="Skills, separated by commas" proposalKey="skills">
          <input
            ref={activeSuggestionKey === 'skills' ? highlightRef : null}
            className={hl('skills')}
            type="text"
            value={skillsText}
            onChange={(e) => update({ skills: e.target.value.split(',').map((x) => x.trim()) })}
            placeholder="JavaScript, React, Node.js, AWS"
          />
        </Field>
      </section>

      <section className="editor-section" id="resume-section-certifications">
        <SectionTitle
          value={labels.certifications}
          hint={SECTION_HINTS.certifications}
          score={scoreFor('certifications')}
          labelKey="label.certifications"
          onChange={(value) => setLabel('certifications', value)}
          actions={(
            <IconButton icon={FiPlus} label="Add certification" size="sm" onClick={addCertification} />
          )}
        />
        <OptionAnchor proposalKey="certifications.add" />
        {(s.certifications || []).map((cert, idx) => (
          <div key={cert.id || idx} className="experience-block">
            <div className="entry-toolbar">
              <IconButton
                icon={FiTrash2}
                label="Remove certification"
                size="sm"
                variant="danger"
                onClick={() => update({ certifications: s.certifications.filter((_, i) => i !== idx) })}
              />
            </div>
            <div className="editor-grid">
              <Field label="Name" proposalKey={`certifications.${cert.id}.name`}>
                <input value={cert.name || ''} onChange={(e) => updateListItem('certifications', idx, { name: e.target.value })} />
              </Field>
              <Field label="Issuer" proposalKey={`certifications.${cert.id}.issuer`}>
                <input value={cert.issuer || ''} onChange={(e) => updateListItem('certifications', idx, { issuer: e.target.value })} />
              </Field>
              <Field label="Date" proposalKey={`certifications.${cert.id}.date`}>
                <input value={cert.date || ''} onChange={(e) => updateListItem('certifications', idx, { date: e.target.value })} />
              </Field>
              </div>
          </div>
        ))}
      </section>

      <section className="editor-section" id="resume-section-languages">
        <SectionTitle
          value={labels.languages}
          hint={SECTION_HINTS.languages}
          score={scoreFor('languages')}
          labelKey="label.languages"
          onChange={(value) => setLabel('languages', value)}
          actions={(
            <IconButton icon={FiPlus} label="Add language" size="sm" onClick={addLanguage} />
          )}
        />
        <OptionAnchor proposalKey="languages.add" />
        {(s.languages || []).map((lang, idx) => (
          <div key={lang.id || idx} className="experience-block">
            <div className="entry-toolbar">
              <IconButton
                icon={FiTrash2}
                label="Remove language"
                size="sm"
                variant="danger"
                onClick={() => update({ languages: s.languages.filter((_, i) => i !== idx) })}
              />
            </div>
            <div className="editor-grid">
              <Field label="Language" proposalKey={`languages.${lang.id}.language`}>
                <input value={lang.language || ''} onChange={(e) => updateListItem('languages', idx, { language: e.target.value })} />
              </Field>
              <Field label="Proficiency" proposalKey={`languages.${lang.id}.proficiency`}>
                <select value={lang.proficiency || ''} onChange={(e) => updateListItem('languages', idx, { proficiency: e.target.value })}>
                  <option value="">Select</option>
                  {['Native', 'Fluent', 'Professional', 'Conversational', 'Basic'].map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </Field>
            </div>
          </div>
        ))}
      </section>

      <section className="editor-section" id="resume-section-projects">
        <SectionTitle
          value={labels.projects}
          hint={SECTION_HINTS.projects}
          score={scoreFor('projects')}
          labelKey="label.projects"
          onChange={(value) => setLabel('projects', value)}
          actions={(
            <IconButton icon={FiPlus} label="Add project" size="sm" onClick={addProject} />
          )}
        />
        <OptionAnchor proposalKey="projects.add" />
        {(s.projects || []).map((project, idx) => (
          <div key={project.id || idx} className="experience-block">
            <div className="entry-toolbar">
              <IconButton
                icon={FiTrash2}
                label="Remove project"
                size="sm"
                variant="danger"
                onClick={() => update({ projects: s.projects.filter((_, i) => i !== idx) })}
              />
            </div>
            <div className="editor-grid">
              <Field label="Project name" proposalKey={`projects.${project.id}.name`}>
                <input value={project.name || ''} onChange={(e) => updateListItem('projects', idx, { name: e.target.value })} />
              </Field>
              <Field label="Technologies" proposalKey={`projects.${project.id}.technologies`}>
                <input
                  value={Array.isArray(project.technologies) ? project.technologies.join(', ') : (project.technologies || '')}
                  onChange={(e) => updateListItem('projects', idx, {
                    technologies: e.target.value.split(',').map((x) => x.trim()).filter(Boolean),
                  })}
                />
              </Field>
            </div>
            <Field label="What you built" proposalKey={`projects.${project.id}.description`}>
              <textarea rows={3} value={project.description || ''} onChange={(e) => updateListItem('projects', idx, { description: e.target.value })} />
            </Field>
          </div>
        ))}
      </section>

      {(s.custom_sections || []).map((section, idx) => {
        const sectionKey = `custom.${section.id}`;
        return (
          <section key={section.id || idx} className="editor-section" id={`resume-section-${section.id || idx}`} ref={activeSuggestionKey === sectionKey ? highlightRef : null}>
            <SectionTitle
              value={section.label || ''}
              hint="A section you added. The title is what appears on the resume."
              labelKey={`custom.${section.id}.label`}
              onChange={(value) => updateListItem('custom_sections', idx, { label: value })}
              actions={(
                <IconButton
                  icon={FiTrash2}
                  label="Remove section"
                  size="sm"
                  variant="danger"
                  onClick={() => update({ custom_sections: s.custom_sections.filter((_, i) => i !== idx) })}
                />
              )}
            />
            <OptionAnchor proposalKey={`custom.${section.id}.body`}>
              <textarea
                className={hl(sectionKey)}
                rows={4}
                value={section.body || ''}
                onChange={(e) => updateListItem('custom_sections', idx, { body: e.target.value })}
                placeholder="Details for this section"
              />
            </OptionAnchor>
          </section>
        );
      })}

      <OptionAnchor proposalKey="custom.add" />
      <div className="editor-add-row">
        <IconButton icon={FiPlus} label="Add section" onClick={addCustomSection} />
      </div>
    </div>
    </FieldProposalContext.Provider>
  );
};

export default ResumeEditor;

export function chatProposalKey(proposal, structured) {
  if (!proposal) return '';
  const section = proposal.section;
  if (section === 'title') return 'title';
  if (section === 'summary') return 'summary';
  if (section === 'skills') return 'skills';
  if (section === 'contact') return `contact.${proposal.field || 'name'}`;
  if (section === 'labels') return `label.${proposal.field || 'summary'}`;
  const listName = {
    experience: 'experience',
    education: 'education',
    certifications: 'certifications',
    languages: 'languages',
    projects: 'projects',
    custom: 'custom_sections',
  }[section];
  if (!listName) return section || '';
  if (proposal.op === 'add') return `${section}.add`;
  const list = structured?.[listName] || [];
  let id = proposal.item_id;
  const known = id && list.some((row) => row.id === id);
  if (!known) {
    if (proposal.item_index != null && list[proposal.item_index]?.id) id = list[proposal.item_index].id;
    else if (list.length === 1) id = list[0].id;
  }
  if (!id) return `${section}.add`;
  if (section === 'experience' && proposal.bullet_index != null) return `experience.${id}.bullet.${proposal.bullet_index}`;
  if (section === 'custom') return `custom.${id}.${proposal.field === 'label' ? 'label' : 'body'}`;
  if (proposal.field) return `${section}.${id}.${proposal.field}`;
  return `${section}.${id}`;
}

export function suggestionToHighlightKey(s, structured) {
  if (!s) return '';
  if (s.section === 'summary') return 'summary';
  if (s.section === 'skills') return 'skills';
  if (s.section === 'contact') return `contact.${s.field || 'name'}`;
  if (s.section === 'custom' && s.item_id) return `custom.${s.item_id}`;

  if (s.section === 'experience') {
    let expId = s.item_id;
    if (!expId && s.exp_index != null && structured?.experience?.[s.exp_index]) {
      expId = structured.experience[s.exp_index].id;
    }
    if (!expId && structured?.experience?.length === 1) {
      expId = structured.experience[0].id;
    }
    if (expId) {
      if (s.bullet_index != null && s.bullet_index >= 0) {
        return `experience.${expId}.bullet.${s.bullet_index}`;
      }
      return `experience.${expId}`;
    }
    return 'experience';
  }

  if (s.section === 'education') {
    let eduId = s.item_id;
    if (!eduId && structured?.education?.length === 1) {
      eduId = structured.education[0].id;
    }
    if (eduId) return `education.${eduId}`;
    return 'education';
  }

  return s.field || s.section || '';
}

export function applySuggestionToStructured(structured, suggestion) {
  if (!suggestion?.proposed_text) return structured;
  const next = JSON.parse(JSON.stringify(structured));
  const text = suggestion.proposed_text;

  if (suggestion.section === 'summary') {
    next.summary = text;
    return next;
  }
  if (suggestion.section === 'skills') {
    next.skills = text.split(',').map((x) => x.trim()).filter(Boolean);
    return next;
  }
  if (suggestion.section === 'custom' && suggestion.item_id) {
    next.custom_sections = (next.custom_sections || []).map((section) => (
      section.id === suggestion.item_id ? { ...section, body: text } : section
    ));
    return next;
  }
  if (suggestion.section === 'contact' && suggestion.field) {
    next.contact = { ...next.contact, [suggestion.field]: text };
    return next;
  }
  if (suggestion.section === 'experience') {
    let idx = next.experience?.findIndex((e) => e.id === suggestion.item_id);
    if (idx < 0 && suggestion.exp_index != null) idx = suggestion.exp_index;
    if (idx < 0 && next.experience?.length === 1) idx = 0;
    if (idx >= 0 && next.experience[idx]) {
      if (suggestion.bullet_index != null && suggestion.bullet_index >= 0) {
        const bullets = [...(next.experience[idx].bullets || [])];
        bullets[suggestion.bullet_index] = text;
        next.experience[idx].bullets = bullets;
      } else if (suggestion.field === 'title') {
        next.experience[idx].title = text;
      } else if (suggestion.field === 'company') {
        next.experience[idx].company = text;
      } else if (suggestion.field === 'location') {
        next.experience[idx].location = text;
      }
    }
    return next;
  }
  return next;
}

function splitLines(text) {
  const lines = String(text || '')
    .split(/\n+/)
    .map((line) => line.replace(/^[-*•]\s*/, '').trim())
    .filter(Boolean);
  return lines.length ? lines : (String(text || '').trim() ? [String(text).trim()] : []);
}

function locateRow(list, update) {
  const items = [...(list || [])];
  if (update.op === 'add') return { items, idx: -1 };
  let idx = update.item_id ? items.findIndex((item) => item.id === update.item_id) : -1;
  if (idx < 0 && Number.isInteger(update.item_index) && items[update.item_index]) idx = update.item_index;
  if (idx < 0 && items.length === 1 && update.op !== 'add') idx = 0;
  return { items, idx };
}

function mergeItem(row, item, fields) {
  const next = { ...row };
  if (!item) return next;
  fields.forEach((field) => {
    const value = item[field];
    if (Array.isArray(value) ? value.length : value) next[field] = value;
  });
  return next;
}

function applyTextField(row, field, text, { listField, append }) {
  const next = { ...row };
  if (listField && field === listField) {
    const incoming = listField === 'technologies'
      ? text.split(',').map((part) => part.trim()).filter(Boolean)
      : splitLines(text);
    next[listField] = append ? [...(next[listField] || []), ...incoming] : incoming;
    return next;
  }
  if (field) next[field] = text;
  return next;
}

export function applyChatUpdates(structured, updates) {
  const next = JSON.parse(JSON.stringify(structured || {}));
  next.contact = { ...(next.contact || {}) };
  next.section_labels = { ...(next.section_labels || {}) };
  next.experience = next.experience || [];
  next.education = next.education || [];
  next.certifications = next.certifications || [];
  next.languages = next.languages || [];
  next.projects = next.projects || [];
  next.custom_sections = next.custom_sections || [];
  next.skills = next.skills || [];
  const touched = [];
  let focus = null;
  const touch = (label) => {
    if (label && !touched.includes(label)) touched.push(label);
  };
  const remember = (target) => {
    if (!focus) focus = target;
  };

  for (const update of updates || []) {
    const op = update.op || 'update';
    const text = String(update.text || '').trim();
    const section = update.section;

    if (section === 'summary') {
      if (!text && !update.item) continue;
      next.summary = text;
      touch('Summary');
      remember({ section: 'summary' });
      continue;
    }

    if (section === 'skills') {
      const incoming = text.split(',').map((part) => part.trim()).filter(Boolean);
      if (op === 'remove') {
        const drop = new Set(incoming.map((part) => part.toLowerCase()));
        next.skills = next.skills.filter((skill) => !drop.has(String(skill).toLowerCase()));
      } else if (op === 'append') {
        next.skills = [...next.skills, ...incoming];
      } else if (incoming.length) {
        next.skills = incoming;
      } else {
        continue;
      }
      touch('Skills');
      remember({ section: 'skills' });
      continue;
    }

    if (section === 'contact') {
      const fields = ['name', 'email', 'phone', 'location', 'linkedin'];
      let changed = false;
      if (update.item && typeof update.item === 'object') {
        for (const field of fields) {
          const value = String(update.item[field] ?? '').trim();
          if (value) {
            next.contact[field] = value;
            changed = true;
          }
        }
      }
      const field = fields.includes(update.field) ? update.field : null;
      if (field && text) {
        next.contact[field] = text;
        changed = true;
      }
      if (!changed) continue;
      touch('Contact');
      remember({ section: 'contact', field: field || 'name' });
      continue;
    }

    if (section === 'labels') {
      const keys = ['contact', 'summary', 'experience', 'education', 'skills', 'certifications', 'languages', 'projects'];
      let changed = false;
      if (update.item && typeof update.item === 'object') {
        for (const key of keys) {
          const value = String(update.item[key] ?? '').trim();
          if (value) {
            next.section_labels[key] = value;
            changed = true;
          }
        }
      }
      if (keys.includes(update.field) && text) {
        next.section_labels[update.field] = text;
        changed = true;
      }
      if (!changed) continue;
      touch('Section titles');
      remember({ section: 'summary' });
      continue;
    }

    if (section === 'title') {
      if (!text) continue;
      next.__fileName = text;
      touch('Resume title');
      continue;
    }

    const specs = {
      experience: {
        label: 'Experience',
        list: 'experience',
        blank: () => ({ id: newExperienceId(), title: '', company: '', location: '', dates: '', bullets: [] }),
        fields: ['title', 'company', 'location', 'dates', 'bullets'],
        listField: 'bullets',
      },
      education: {
        label: 'Education',
        list: 'education',
        blank: () => ({ id: newEducationId(), degree: '', school: '', dates: '' }),
        fields: ['degree', 'school', 'dates'],
      },
      certifications: {
        label: 'Certifications',
        list: 'certifications',
        blank: () => ({ id: newCertificationId(), name: '', issuer: '', date: '' }),
        fields: ['name', 'issuer', 'date'],
      },
      languages: {
        label: 'Languages',
        list: 'languages',
        blank: () => ({ id: newLanguageId(), language: '', proficiency: '' }),
        fields: ['language', 'proficiency'],
      },
      projects: {
        label: 'Projects',
        list: 'projects',
        blank: () => ({ id: newProjectId(), name: '', description: '', technologies: [] }),
        fields: ['name', 'description', 'technologies'],
        listField: 'technologies',
      },
      custom: {
        label: 'Custom section',
        list: 'custom_sections',
        blank: () => ({ id: newCustomSectionId(), label: 'New section', body: '' }),
        fields: ['label', 'body'],
      },
    };
    const spec = specs[section];
    if (!spec) continue;

    if (op === 'add') {
      const created = mergeItem(spec.blank(), update.item, spec.fields);
      if (text) {
        const field = spec.fields.includes(update.field) ? update.field : spec.fields[spec.fields.length - 1];
        Object.assign(created, applyTextField(created, field, text, { listField: spec.listField, append: false }));
      }
      next[spec.list] = [...next[spec.list], created];
      touch(created.label || spec.label);
      remember({
        section,
        item_id: created.id,
        field: update.field,
        bullet_index: update.bullet_index,
      });
      continue;
    }

    const placed = locateRow(next[spec.list], update);
    if (placed.idx < 0) continue;
    if (op === 'remove') {
      placed.items.splice(placed.idx, 1);
      next[spec.list] = placed.items;
      touch(spec.label);
      remember({ section });
      continue;
    }

    let row = mergeItem(placed.items[placed.idx], update.item, spec.fields);
    if (spec.listField && update.bullet_index != null && text) {
      const values = [...(row[spec.listField] || [])];
      if (op === 'append' || update.bullet_index >= values.length) values.push(text);
      else values[update.bullet_index] = text;
      row = { ...row, [spec.listField]: values };
    } else if (text) {
      const field = spec.fields.includes(update.field)
        ? update.field
        : (spec.listField || spec.fields[0]);
      row = applyTextField(row, field, text, {
        listField: spec.listField,
        append: op === 'append',
      });
    }
    placed.items[placed.idx] = row;
    next[spec.list] = placed.items;
    touch(row.label || spec.label);
    remember({
      section,
      item_id: row.id,
      field: update.field,
      bullet_index: update.bullet_index,
    });
  }

  const fileName = next.__fileName || null;
  delete next.__fileName;
  return { structured: next, touched, focus, fileName };
}
